/**
 * PICK PEOPLE (design_handoff_club_pages/README.md §2 SEND): the picker under the TO row when
 * a send goes to hand-picked people. Everyone the page can reach — members / followers and
 * the contact list — in one pool, with picked chips (tap to remove), PICK ALL SHOWN / UNPICK
 * SHOWN, CLEAR, a search, filters ALL · MEMBERS · HAS EMAIL · HAS PHONE · <groups>, and per-row
 * channel badges NOTIF · EMAIL · TEXT (black = the selected channel, greyed = unreachable).
 *
 * `picked` is the list of AudiencePersonDto rows picked ({ kind, id, name, … }); `onChange`
 * gets the next list. The pool is loaded once (the endpoint lists up to 1000) and filtered here.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { audienceService } from '../../services';
import { RoundCheck, Face, INK, MUTED, SUBTLE, INSET, RED, label as lbl } from '../club/clubUi';

const CHANNELS = [['NOTIF', 'notification', (p) => !!p.onPortal], ['EMAIL', 'email', (p) => !!p.hasEmail], ['TEXT', 'sms', (p) => !!p.hasPhone]];

/** Can this person get a message on this channel? */
export const reachable = (p, channel) => (channel === 'notification' ? !!p.onPortal : channel === 'sms' ? !!p.hasPhone : !!p.hasEmail);

export const personKey = (p) => `${p.kind}-${p.id}`;

export default function PickPeople({ ownerType, ownerId, channel = 'notification', groups = [], picked = [], onChange, followerWord = 'MEMBERS', pool: poolProp = null }) {
  const [pool, setPool] = useState(poolProp);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (poolProp) { setPool(poolProp); return undefined; }
    let live = true;
    audienceService.searchPeople(ownerType, ownerId, '', 1000).then((r) => { if (live) setPool(r.success ? (r.data || []) : []); });
    return () => { live = false; };
  }, [ownerType, ownerId, poolProp]);

  const pickedKeys = useMemo(() => new Set(picked.map(personKey)), [picked]);
  const needle = q.trim().toLowerCase();
  const shown = useMemo(() => (pool || []).filter((p) => {
    if (filter === 'members' && !p.isMember) return false;
    if (filter === 'email' && !p.hasEmail) return false;
    if (filter === 'phone' && !p.hasPhone) return false;
    if (!['all', 'members', 'email', 'phone'].includes(filter) && !(p.groupIds || []).includes(filter)) return false;
    if (needle && !`${p.name} ${p.sub || ''}`.toLowerCase().includes(needle)) return false;
    return true;
  }), [pool, filter, needle]);

  const toggle = (p) => {
    const k = personKey(p);
    onChange?.(pickedKeys.has(k) ? picked.filter((x) => personKey(x) !== k) : [...picked, p]);
  };
  const allShown = shown.length > 0 && shown.every((p) => pickedKeys.has(personKey(p)));
  const toggleAll = () => {
    if (allShown) { const drop = new Set(shown.map(personKey)); onChange?.(picked.filter((x) => !drop.has(personKey(x)))); return; }
    const next = [...picked];
    shown.forEach((p) => { if (!pickedKeys.has(personKey(p))) next.push(p); });
    onChange?.(next);
  };

  const filters = [['all', 'ALL'], ['members', followerWord], ['email', 'HAS EMAIL'], ['phone', 'HAS PHONE'], ...groups.map((g) => [g.id, String(g.name).toUpperCase()])];
  const small = (on) => ({ height: 30, padding: '0 12px', border: 'none', borderRadius: 999, background: on ? INK : '#fff', color: on ? '#fff' : INK, fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0 });

  return (
    <div className="flex flex-col font-mono" style={{ gap: 10, padding: 14, borderRadius: 20, background: INSET }} data-testid="pick-people">
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
        <span style={lbl(10, '.16em')} data-testid="pick-title">{picked.length ? `${picked.length} PICKED` : 'PICK WHO GETS IT'}</span>
        <span className="flex" style={{ gap: 6 }}>
          <button type="button" onClick={toggleAll} style={small(false)} data-testid="pick-all">{allShown ? 'UNPICK SHOWN' : 'PICK ALL SHOWN'}</button>
          {picked.length > 0 && <button type="button" onClick={() => onChange?.([])} style={{ ...small(false), color: RED }} data-testid="pick-clear">CLEAR</button>}
        </span>
      </span>
      {picked.length > 0 && (
        <span className="flex flex-wrap" style={{ gap: 6 }} data-testid="picked-chips">
          {picked.map((p) => (
            <button key={personKey(p)} type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.name}`} className="flex items-center" style={{ gap: 8, height: 32, padding: '0 8px 0 4px', border: 'none', borderRadius: 999, background: INK, color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
              <Face src={p.avatarUrl} name={p.name} size={24} style={{ background: p.avatarUrl ? undefined : '#333', color: '#fff', fontSize: 10 }} />
              {String(p.name).split(' ')[0]}<span style={{ opacity: 0.6 }}>✕</span>
            </button>
          ))}
        </span>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search your contacts and ${followerWord.toLowerCase()}`} aria-label="Search people" style={{ height: 44, padding: '0 18px', border: 'none', borderRadius: 999, background: '#fff', fontFamily: 'inherit', fontSize: 13, color: INK, outline: 'none' }} data-testid="pick-search" />
      <span className="flex" style={{ gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {filters.map(([k, l]) => <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)} style={{ ...small(filter === k), fontWeight: 700, letterSpacing: '.1em' }}>{l}</button>)}
      </span>
      <div className="flex flex-col" style={{ maxHeight: 320, overflow: 'auto', borderRadius: 16, background: '#fff' }} data-testid="pick-rows">
        {pool === null && <span style={{ padding: 20, textAlign: 'center', fontSize: 12, color: MUTED }}>Loading…</span>}
        {pool !== null && shown.length === 0 && <span style={{ padding: 20, textAlign: 'center', fontSize: 12, color: MUTED }}>Nobody matches.</span>}
        {shown.map((p, i) => {
          const on = pickedKeys.has(personKey(p));
          return (
            <div key={personKey(p)} onClick={() => toggle(p)} aria-selected={on} className="flex items-center text-left" style={{ gap: 12, padding: '10px 14px', borderTop: i ? '2px solid #F2F2F2' : 'none', background: on ? '#F0FEFF' : '#fff', cursor: 'pointer' }} data-testid="pick-row" data-picked={on ? '1' : '0'}>
              <RoundCheck on={on} onChange={() => toggle(p)} label={`Pick ${p.name}`} />
              <Face src={p.avatarUrl} name={p.name} size={34} tintKey={i} />
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{p.name}</span>
                <span className="truncate" style={{ fontSize: 10, color: MUTED }}>{[p.isMember ? followerWord.replace(/S$/, '') : null, p.sub].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="flex flex-none" style={{ gap: 3 }}>
                {CHANNELS.map(([l, k, ok]) => {
                  const can = ok(p);
                  return <span key={k} style={{ padding: '3px 7px', borderRadius: 999, background: !can ? '#F5F5F5' : k === channel ? INK : '#E5E7EB', color: !can ? '#D1D5DB' : k === channel ? '#fff' : INK, fontSize: 8, fontWeight: 800, letterSpacing: '.1em' }} data-testid={`chan-${k}`} data-reachable={can ? '1' : '0'}>{l}</span>;
                })}
              </span>
            </div>
          );
        })}
      </div>
      <span style={{ fontSize: 10, color: SUBTLE }}>{channel === 'notification' ? `A notification reaches whoever is on Portal.` : channel === 'sms' ? 'A text reaches whoever has a phone number.' : 'An email reaches whoever has an email.'}</span>
    </div>
  );
}
